import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Trash2, Search, Filter } from 'lucide-react';
import { api } from '../utils/api.js';
import { formatBytes, formatIST } from '../utils/formatters.js';
import Card from '../components/ui/Card.jsx';
import Button from '../components/ui/Button.jsx';
import Input from '../components/ui/Input.jsx';
import ConfirmDialog from '../components/ui/ConfirmDialog.jsx';
import { useMutation, useQueryClient } from '@tanstack/react-query';

function StorageBar({ used, quota }) {
  const pct = Math.min((used / quota) * 100, 100);
  const color = pct > 85 ? 'bg-red-500' : pct > 65 ? 'bg-orange-400' : 'bg-teal-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>{formatBytes(used)} used</span>
        <span>{formatBytes(quota)} quota</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-gray-400">{pct.toFixed(1)}% of 9 GB used</p>
    </div>
  );
}

export default function FileManagerPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({ search: '', fileType: '', page: 1 });
  const [deleteTarget, setDeleteTarget] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['files', filters],
    queryFn: () => api.get('/files', { params: filters }).then(r => r.data.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/files/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['files'] }); setDeleteTarget(null); },
  });

  const handleDownload = async (id) => {
    const { data } = await api.get(`/files/${id}/download`);
    window.open(data.data.url, '_blank');
  };

  const updateFilter = (key, val) => setFilters(f => ({ ...f, [key]: val, page: 1 }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">File Manager</h1>
          <p className="text-sm text-gray-500 mt-0.5">All uploaded attachments across gate passes and delivery notes</p>
        </div>
      </div>

      {/* Storage usage */}
      {data && (
        <Card title="R2 Storage Usage">
          <div className="p-6">
            <StorageBar used={data.totalStorage} quota={data.quota} />
            <p className="text-xs text-gray-400 mt-2">{data.total} files · Auto-cleanup triggers above 9 GB</p>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-wrap gap-3 border-b">
          <div className="flex-1 min-w-48">
            <Input
              placeholder="Search by file name or gate pass…"
              value={filters.search}
              onChange={e => updateFilter('search', e.target.value)}
            />
          </div>
          <select
            value={filters.fileType}
            onChange={e => updateFilter('fileType', e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">All file types</option>
            <option value="image/">Images</option>
            <option value="application/pdf">PDFs</option>
            <option value="wordprocessingml">Word Docs</option>
            <option value="spreadsheetml">Excel Files</option>
          </select>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['File Name','Type','Size','Gate Pass / DN','Uploaded By','Date',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.items || []).map(f => (
                  <tr key={f.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium max-w-xs truncate">{f.originalName}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs uppercase">{f.mimeType.split('/').pop()}</td>
                    <td className="px-4 py-3 text-gray-500">{formatBytes(f.sizeBytes)}</td>
                    <td className="px-4 py-3">
                      {f.gatePass ? (
                        <span className="text-xs font-mono text-teal-700">{f.gatePass.number}</span>
                      ) : f.deliveryNote ? (
                        <span className="text-xs font-mono text-blue-700">{f.deliveryNote.number}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{f.uploadedBy?.name}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{formatIST(f.uploadedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => handleDownload(f.id)} className="text-gray-400 hover:text-teal-600">
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteTarget(f)} className="text-gray-400 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.items?.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No files found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {data && data.total > data.limit && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-sm text-gray-500">
            <span>{data.total} total files</span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={data.page <= 1} onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}>Previous</Button>
              <Button variant="secondary" size="sm" disabled={data.page * data.limit >= data.total} onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}>Next</Button>
            </div>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMut.mutate(deleteTarget.id)}
        loading={deleteMut.isPending}
        title="Delete File"
        message={`Permanently delete "${deleteTarget?.originalName}"? The file will be removed from R2 storage.`}
        confirmLabel="Delete"
      />
    </div>
  );
}
